
import { defineComponent, ref, computed, watch, nextTick, triggerRef, onMounted, getCurrentInstance } from 'vue'
import BaseInputOutlined from '@/components/inputs/BaseInputOutlined.vue'
import CustomActivatorSelect from '@/components/inputs/CustomActivatorSelect.vue'
import TaskSelector from './TaskSelector.vue'
import DeleteIcon from '../common/DeleteIcon.vue'
import EditIcon from '../common/EditIcon.vue'
import InfoIcon from '../common/InfoIcon.vue'
import { Plan } from '../../types/plan.types'
import { planService } from '../../services/planService'
import { CONNECTOR_TYPES } from '@root/src/vars/general'
import { getConnectionStatus } from '@/api/connectors/yClients'

export default defineComponent({
    name: 'PlanSettingsModal',
    components: {
        BaseInputOutlined,
        CustomActivatorSelect,
        TaskSelector,
        DeleteIcon,
        EditIcon,
        InfoIcon,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        plan: {
            type: Object as () => Plan | null,
            default: null,
        },
        availableTasks: {
            type: Array,
            default: () => [],
        },
        availableBranches: {
            type: Array,
            default: () => [],
        },
        availableUsers: {
            type: Array,
            default: () => [],
        },
        availableCrmUsers: {
            type: Array,
            default: () => [],
        },
        projectName: {
            type: String,
            default: '',
        },
        projectId: {
            type: String,
            default: null,
        },
        createTaskFn: {
            type: Function,
            default: null,
        },
    },
    emits: ['close', 'save', 'create-task', 'update-task', 'delete-task'],
    setup(props, { emit }) {
        const instance = getCurrentInstance()
        const store = instance?.proxy?.$store

        const isVisible = computed({
            get: () => props.visible,
            set: value => {
                if (!value) emit('close')
            },
        })

        const isEdit = computed(() => !!props.plan)

        const isReadOnly = computed(() => {
            return props.plan && props.plan.status === 'published'
        })

        // Проверка наличия подключения к yClients
        const hasYClientsConnection = computed(() => {
            if (!props.projectId || !store) return false
            try {
                const project = store.getters.projectById(props.projectId)
                if (!project || !project.connectors) return false
                const yClientsConnector = project.connectors.find(
                    (connector: any) => connector.connectorType === CONNECTOR_TYPES.yClients
                )
                return !!yClientsConnector
            } catch (error) {
                console.warn('Failed to check yClients connection:', String(error))
                return false
            }
        })

        const planData = ref({
            name: '',
            tasks: [{ id: '', name: '' }],
            branches: [{ id: '', name: '' }],
            channels: [{ id: '', name: '' }],
            userMappings: [
                {
                    dhUser: { id: '', name: '' },
                    crmUser: { id: '', name: '' },
                },
            ],
        })

        // Статус авторизации коннектора yClients
        const yClientsConnectorAuthorized = ref(false)
        // Статус подключения коннектора yClients ('active' | 'freezed' | 'uninstalled' | null)
        const yClientsConnectionStatus = ref<'active' | 'freezed' | 'uninstalled' | null>(null)

        // Отображаемые значения для полей ввода
        const taskDisplayValue = ref(['Выберите задачу'])
        const userDisplayValue = ref(['Выберите пользователя'])
        const crmUserDisplayValue = ref(['Выберите сотрудника'])

        // Отображаемые значения для полей добавления
        const addTaskDisplayValue = ref('Добавить ещё')
        const addUserDisplayValue = ref('Добавить ещё')
        const addCrmUserDisplayValue = ref('Добавить ещё')

        // Переменные для новых инпутов
        const addBranchName = ref('')

        // Переменные для модалки редактирования задачи
        const showTaskEditModal = ref(false)
        const selectedTask = ref<any>(null)
        const availableServices = ref<Array<{ id: string; name: string; category?: string }>>([])
        const servicesLoading = ref(false)

        // Функция для проверки статуса авторизации коннектора yClients
        const checkYClientsAuthorization = async () => {
            if (!props.projectId || !store) {
                yClientsConnectorAuthorized.value = false
                yClientsConnectionStatus.value = null
                return
            }

            try {
                const project = store.getters.projectById(props.projectId)
                if (!project || !project.connectors) {
                    yClientsConnectorAuthorized.value = false
                    yClientsConnectionStatus.value = null
                    return
                }

                const yClientsConnector = project.connectors.find(
                    (connector: any) => connector.connectorType === CONNECTOR_TYPES.yClients
                )

                if (!yClientsConnector || !yClientsConnector.connectorId) {
                    yClientsConnectorAuthorized.value = false
                    yClientsConnectionStatus.value = null
                    return
                }

                // Проверяем статус подключения через API
                const { data, error } = await getConnectionStatus(yClientsConnector.connectorId)
                
                if (error || !data) {
                    yClientsConnectorAuthorized.value = false
                    yClientsConnectionStatus.value = null
                    return
                }

                // Сохраняем статус подключения
                yClientsConnectionStatus.value = data.status
                // Коннектор авторизирован, если статус 'active'
                yClientsConnectorAuthorized.value = data.status === 'active'
            } catch (error) {
                console.warn('Failed to check yClients authorization:', String(error))
                yClientsConnectorAuthorized.value = false
                yClientsConnectionStatus.value = null
            }
        }

        // Инициализация данных при открытии модального окна
        watch(
            () => props.visible,
            async newVal => {
                if (newVal) {
                    // Проверяем авторизацию коннектора yClients
                    await checkYClientsAuthorization()

                    // Загружаем userMappings из БД для конкретного плана (если редактируем) или пустой массив (если создаем)
                    let loadedUserMappings: Array<{
                        dhUser: { id: string; name?: string }
                        crmUser: { id: string; name?: string }
                    }> = []
                    try {
                        // Если редактируем план, загружаем его userMappings, иначе пустой массив
                        if (props.plan && props.plan.id) {
                            loadedUserMappings = await planService.getUserMappings(props.projectId, props.plan.id)
                        }
                        // Заполняем имена пользователей из availableUsers и availableCrmUsers
                        const dhUsers = props.availableUsers as Array<{ id: string; name: string }>
                        const crmUsers = props.availableCrmUsers as Array<{ id: string; name: string }>
                        loadedUserMappings = loadedUserMappings.map(mapping => {
                            const dhUser = dhUsers.find(u => u.id === mapping.dhUser?.id)
                            const crmUser = crmUsers.find(u => u.id === mapping.crmUser?.id)
                            return {
                                dhUser: {
                                    id: mapping.dhUser?.id || '',
                                    name: dhUser?.name || mapping.dhUser?.name || '',
                                },
                                crmUser: {
                                    id: mapping.crmUser?.id || '',
                                    name: crmUser?.name || mapping.crmUser?.name || '',
                                },
                            }
                        })
                    } catch (error) {
                        console.warn('Failed to load user mappings:', String(error))
                        // Если не удалось загрузить, используем пустой массив
                        loadedUserMappings = [{ dhUser: { id: '', name: '' }, crmUser: { id: '', name: '' } }]
                    }

                    // Модалка открыта с доступными задачами
                    if (props.plan) {
                        // Режим редактирования или копирования
                        // Заполнение формы данными плана
                        planData.value = {
                            name: props.plan.name || '',
                            tasks: Array.isArray(props.plan.tasks)
                                ? props.plan.tasks.map((task: any) => {
                                      // Ищем полную информацию о задаче в availableTasks
                                      const availableTask = Array.isArray(props.availableTasks)
                                          ? props.availableTasks.find((t: any) => t.id === task.id)
                                          : null

                                      return {
                                          id: task.id,
                                          name: availableTask ? (availableTask as any).name : task.name,
                                          unit: availableTask
                                              ? (availableTask as any).unit || ''
                                              : task.unit || '',
                                          description: availableTask
                                              ? (availableTask as any).description || ''
                                              : task.description || '',
                                          taskNumber: availableTask
                                              ? (availableTask as any).taskNumber ||
                                                (availableTask as any).task_number
                                              : task.taskNumber || task.task_number,
                                          autofillParams: availableTask
                                              ? (availableTask as any).autofillParams ||
                                                (availableTask as any).autofill_params
                                              : task.autofillParams || task.autofill_params,
                                          // Сохраняем существующие norms, limits и significance из исходного плана
                                          norms: task.norms || {},
                                          limits: task.limits || {},
                                          significance: task.significance || 0,
                                      }
                                  })
                                : Array.isArray(props.plan.taskIds) && props.plan.taskIds.length > 0
                                ? props.plan.taskIds.map((taskId: string) => {
                                      // Ищем задачу в availableTasks по ID
                                      const availableTask = Array.isArray(props.availableTasks)
                                          ? props.availableTasks.find((task: any) => task.id === taskId)
                                          : null

                                      // Ищем исходную задачу в плане для сохранения norms и limits
                                      const originalTask = (props.plan as any).tasks?.find(
                                          (t: any) => t.id === taskId
                                      )

                                      return {
                                          id: taskId,
                                          name: availableTask ? (availableTask as any).name : taskId, // Используем название из availableTasks или ID
                                          unit: availableTask ? (availableTask as any).unit || '' : '',
                                          description: availableTask
                                              ? (availableTask as any).description || ''
                                              : '',
                                          taskNumber: availableTask
                                              ? (availableTask as any).taskNumber ||
                                                (availableTask as any).task_number
                                              : originalTask?.taskNumber || originalTask?.task_number,
                                          autofillParams: availableTask
                                              ? (availableTask as any).autofillParams ||
                                                (availableTask as any).autofill_params
                                              : originalTask?.autofillParams || originalTask?.autofill_params,
                                          // Сохраняем существующие norms и limits из исходного плана
                                          norms: originalTask?.norms || {},
                                          limits: originalTask?.limits || {},
                                      }
                                  })
                                : [{ id: '', name: '', unit: '', description: '' }],
                            branches: Array.isArray(props.plan.branchIds)
                                ? props.plan.branchIds.map((id: string, index: number) => ({
                                      id: index === 0 ? props.projectName : id, // Первый филиал - название проекта
                                      name: index === 0 ? props.projectName : id,
                                  }))
                                : [{ id: props.projectName, name: props.projectName }],
                            channels: [{ id: 'general', name: 'general' }],
                            userMappings:
                                loadedUserMappings.length > 0
                                    ? loadedUserMappings
                                    : [{ dhUser: { id: '', name: '' }, crmUser: { id: '', name: '' } }],
                            // Инициализируем поля для шкал и зарплаты
                            salary: (props.plan as any).salary || getDefaultSalary(),
                            revenueScale:
                                (props.plan as any).revenueScale ||
                                (() => {
                                    // Для существующих планов без revenueScale
                                    const defaultScale = getDefaultRevenueScale()

                                    // Если это новый план с одним филиалом, создаем массив
                                    if (planData.value.branches && planData.value.branches.length === 1) {
                                        return defaultScale
                                    } else {
                                        // Если несколько филиалов, создаем объект с ключами для каждого филиала
                                        const revenueScale: any = {}
                                        revenueScale[props.projectName] = defaultScale
                                        return revenueScale
                                    }
                                })(),
                            kpiScale: (props.plan as any).kpiScale || getDefaultKpiScale(),
                            dailyRevenuePlan: (props.plan as any).dailyRevenuePlan || {},
                            branchRevenueScales: (props.plan as any).branchRevenueScales || {},
                        } as any
                        // Инициализируем отображаемые значения
                        taskDisplayValue.value = planData.value.tasks.map(
                            task => task.name || 'Выберите задачу'
                        )
                        userDisplayValue.value = planData.value.userMappings.map(
                            mapping => mapping.dhUser.name || 'Выберите пользователя'
                        )
                        crmUserDisplayValue.value = planData.value.userMappings.map(
                            mapping => mapping.crmUser.name || 'Выберите сотрудника'
                        )
                        // Устанавливаем равные значения значимости для всех задач, если они не установлены
                        setEqualSignificanceForTasks(planData.value.tasks)

                        // Принудительно обновляем реактивность
                        triggerRef(planData)
                    } else {
                        // Режим создания
                        planData.value = {
                            name: '',
                            tasks: [{ id: '', name: '', unit: '', description: '', significance: 0 }],
                            branches: [{ id: props.projectName, name: props.projectName }],
                            userMappings:
                                loadedUserMappings.length > 0
                                    ? loadedUserMappings
                                    : [{ dhUser: { id: '', name: '' }, crmUser: { id: '', name: '' } }],
                            channels: [{ id: '', name: '' }],
                            // Инициализируем поля для шкал и зарплаты
                            salary: getDefaultSalary(),
                            revenueScale: (() => {
                                // Для новых планов
                                const defaultScale = getDefaultRevenueScale()

                                // Если это новый план с одним филиалом, создаем массив
                                if (planData.value.branches && planData.value.branches.length === 1) {
                                    return defaultScale
                                } else {
                                    // Если несколько филиалов, создаем объект с ключами для каждого филиала
                                    const revenueScale: any = {}
                                    revenueScale[props.projectName] = defaultScale
                                    return revenueScale
                                }
                            })(),
                            kpiScale: getDefaultKpiScale(),
                            dailyRevenuePlan: {},
                            branchRevenueScales: {},
                        } as any
                        // Инициализируем отображаемые значения
                        taskDisplayValue.value = ['Выберите задачу']
                        userDisplayValue.value = ['Выберите пользователя']
                        crmUserDisplayValue.value = ['Выберите сотрудника']
                    }

                    // Инициализируем данные по умолчанию для всех планов
                    initializeDefaultData()
                }
            }
        )

        const isFormValid = computed(() => {
            return planData.value.name.trim() !== '' && planData.value.tasks.some(task => task.id !== '')
        })

        // Методы для работы с задачами
        const updateTask = (index: number, taskId: string) => {
            const task = props.availableTasks.find((t: any) => t.id === taskId) as any
            if (task) {
                planData.value.tasks[index] = {
                    id: task.id,
                    name: task.name,
                    unit: task.unit,
                    taskNumber: task.taskNumber || task.task_number,
                    autofillParams: task.autofillParams || task.autofill_params,
                } as any
                // Обновляем отображаемое значение
                taskDisplayValue.value[index] = task.name
                // Принудительно обновляем реактивность
                triggerRef(planData)

                // Синхронизируем tasks с taskIds
                synchronizeTasksWithTaskIds()
            }
        }

        const addTask = () => {
            if (planData.value.tasks.length < 5) {
                planData.value.tasks.push({ id: '', name: '' })
                taskDisplayValue.value.push('Выберите задачу')
                triggerRef(planData)
            }
        }

        const addTaskFromSelect = (taskId: string) => {
            if (taskId && planData.value.tasks.length < 5) {
                const task = props.availableTasks.find((t: any) => t.id === taskId) as any
                if (task) {
                    planData.value.tasks.push({
                        id: task.id,
                        name: task.name,
                        unit: task.unit,
                        taskNumber: task.taskNumber || task.task_number,
                        autofillParams: task.autofillParams || task.autofill_params,
                    } as any)
                    taskDisplayValue.value.push(task.name)
                    // Сбрасываем поле добавления
                    addTaskDisplayValue.value = 'Добавить ещё'
                    triggerRef(planData)

                    // Синхронизируем tasks с taskIds
                    synchronizeTasksWithTaskIds()
                }
            }
        }

        const removeTask = (index: number) => {
            if (planData.value.tasks.length > 1) {
                planData.value.tasks.splice(index, 1)
                taskDisplayValue.value.splice(index, 1)
                triggerRef(planData)

                // Синхронизируем tasks с taskIds
                synchronizeTasksWithTaskIds()
            }
        }

        // Определяем, какие переменные услуг нужны для задачи
        const needsVisitedAndBookedServices = computed(() => {
            if (!selectedTask.value || !selectedTask.value.autofillParams) return false
            return selectedTask.value.autofillParams.function === 'ba_task_conv_service_opz'
        })

        const needsRecordServiceList = computed(() => {
            if (!selectedTask.value || !selectedTask.value.autofillParams) return false
            return selectedTask.value.autofillParams.function === 'ba_task_daily_sales_sum_for_admin'
        })

        const hasServiceVariables = computed(() => {
            return needsVisitedAndBookedServices.value || needsRecordServiceList.value
        })

        // Загружаем список услуг
        const loadServices = async () => {
            if (!props.projectId || servicesLoading.value) return

            try {
                servicesLoading.value = true
                const services = await planService.getServiceList(props.projectId)
                availableServices.value = services || []
            } catch (error) {
                console.error('Error loading services:', String(error))
                availableServices.value = []
            } finally {
                servicesLoading.value = false
            }
        }

        // Инициализируем autofillParams для задачи, если их нет
        const initializeAutofillParams = (task: any) => {
            if (!task.autofillParams) {
                task.autofillParams = {}
            }

            // Инициализируем массивы услуг, если их нет
            if (task.autofillParams.function === 'ba_task_conv_service_opz') {
                if (!task.autofillParams.visitedServices) {
                    task.autofillParams.visitedServices = []
                }
                if (!task.autofillParams.bookedServices) {
                    task.autofillParams.bookedServices = []
                }
            } else if (task.autofillParams.function === 'ba_task_daily_sales_sum_for_admin') {
                if (!task.autofillParams.recordServiceList) {
                    task.autofillParams.recordServiceList = []
                }
            }
        }

        // Удаление услуги из списка
        const removeService = (
            serviceId: string,
            type: 'visitedServices' | 'bookedServices' | 'recordServiceList'
        ) => {
            if (!selectedTask.value || !selectedTask.value.autofillParams) return

            if (type === 'visitedServices' && selectedTask.value.autofillParams.visitedServices) {
                selectedTask.value.autofillParams.visitedServices = selectedTask.value.autofillParams.visitedServices.filter(
                    (id: string) => id !== serviceId
                )
            } else if (type === 'bookedServices' && selectedTask.value.autofillParams.bookedServices) {
                selectedTask.value.autofillParams.bookedServices = selectedTask.value.autofillParams.bookedServices.filter(
                    (id: string) => id !== serviceId
                )
            } else if (type === 'recordServiceList' && selectedTask.value.autofillParams.recordServiceList) {
                selectedTask.value.autofillParams.recordServiceList = selectedTask.value.autofillParams.recordServiceList.filter(
                    (id: string) => id !== serviceId
                )
            }
        }

        // Отслеживаем открытие модалки редактирования задачи и загружаем услуги
        watch(showTaskEditModal, async isOpen => {
            if (isOpen && hasServiceVariables.value) {
                await loadServices()
            }
        })

        const editTask = async (task: any) => {
            // Ищем полную информацию о задаче в availableTasks
            const availableTask = Array.isArray(props.availableTasks)
                ? (props.availableTasks as any[]).find((t: any) => t.id === task.id)
                : null

            // Открываем модалку редактирования задачи
            selectedTask.value = {
                ...task,
                name: availableTask ? (availableTask as any).name : task.name,
                unit: availableTask ? (availableTask as any).unit || '' : task.unit || '',
                description: availableTask
                    ? (availableTask as any).description || ''
                    : task.description || '',
                taskNumber: availableTask
                    ? (availableTask as any).taskNumber || (availableTask as any).task_number
                    : task.taskNumber || task.task_number,
                autofillParams: availableTask
                    ? (availableTask as any).autofillParams ||
                      (availableTask as any).autofill_params ||
                      task.autofillParams ||
                      task.autofill_params
                    : task.autofillParams || task.autofill_params,
                // Сохраняем оригинальный индекс для обновления после сохранения
                originalTaskIndex: planData.value.tasks.findIndex(
                    t => t.id === task.id || t.name === task.name
                ),
            }

            // Парсим autofillParams, если это строка
            if (selectedTask.value.autofillParams && typeof selectedTask.value.autofillParams === 'string') {
                try {
                    selectedTask.value.autofillParams = JSON.parse(selectedTask.value.autofillParams)
                } catch (e) {
                    selectedTask.value.autofillParams = {}
                }
            }

            // Инициализируем autofillParams
            initializeAutofillParams(selectedTask.value)

            showTaskEditModal.value = true

            // Загружаем услуги, если задача содержит переменные услуг
            if (hasServiceVariables.value) {
                await loadServices()
            }
        }

        // Новые методы для TaskSelector
        const addNewTask = () => {
            // Открываем модалку создания новой задачи
            selectedTask.value = {
                id: '',
                name: '',
                unit: '',
                description: '',
                originalTaskIndex: -1, // -1 означает создание новой задачи
            }
            showTaskEditModal.value = true
        }

        const deleteTaskFromSelector = (task: any) => {
            // Удаляем задачу из availableTasks (это задача проекта)
            emit('delete-task', task)

            // Принудительно обновляем компонент после удаления
            nextTick(() => {
                triggerRef(planData)
            })
        }

        // Функция для установки равных значений значимости для всех задач
        const setEqualSignificanceForTasks = (tasks: any[]) => {
            if (!tasks || tasks.length === 0) {
                return
            }

            // Проверяем, есть ли уже установленные значения значимости
            const hasExistingSignificance = tasks.some(task => task.significance && task.significance > 0)

            // Если значения значимости уже установлены, не перезаписываем их
            if (hasExistingSignificance) {
                return
            }

            // Вычисляем равную значимость для всех задач
            const equalSignificance = Math.round((100 / tasks.length) * 10) / 10

            // Устанавливаем равную значимость для всех задач
            tasks.forEach(task => {
                task.significance = equalSignificance
            })

            // Корректируем последнюю задачу, чтобы сумма была точно 100%
            const currentSum = tasks.reduce((sum, task) => sum + (task.significance || 0), 0)
            const difference = 100 - currentSum

            if (tasks.length > 0 && Math.abs(difference) > 0.01) {
                const lastTask = tasks[tasks.length - 1]
                lastTask.significance = Math.round((lastTask.significance + difference) * 10) / 10
            }
        }

        // Функция для полной синхронизации tasks с taskIds
        const synchronizeTasksWithTaskIds = () => {
            if (!props.plan || !Array.isArray((props.plan as any).tasks)) {
                return
            }

            const currentTaskIds = planData.value.tasks.filter(task => task.id).map(task => task.id)
            const originalTasks = (props.plan as any).tasks

            // Создаем новый массив задач, синхронизированный с taskIds
            const synchronizedTasks = currentTaskIds.map(taskId => {
                    // Ищем существующую задачу в исходном плане
                    const existingTask = originalTasks.find((task: any) => task.id === taskId)

                    if (existingTask) {
                        // Если задача существует, возвращаем её данные
                        return {
                            ...existingTask,
                            // Сохраняем все существующие данные: нормы, лимиты, веса и т.д.
                            norm: existingTask.norm || 0,
                            weight: existingTask.weight || 0,
                            maxCompletion: existingTask.maxCompletion || 100,
                            limits: existingTask.limits || {},
                        }
                    } else {
                        // Если задача новая, создаем с данными по умолчанию
                        const availableTask = props.availableTasks.find((t: any) => t.id === taskId)

                        // Создаем структуру norms и limits для всех филиалов и каналов
                        const norms: any = {}
                        const limits: any = {}

                        planData.value.branches.forEach((branch: any) => {
                            if (!norms[branch.id]) {
                                norms[branch.id] = {}
                            }
                            if (!limits[branch.id]) {
                                limits[branch.id] = {}
                            }

                            planData.value.channels.forEach((channel: any) => {
                                // Устанавливаем значения по умолчанию только если их еще нет
                                if (norms[branch.id][channel.id] === undefined) {
                                    norms[branch.id][channel.id] = 0
                                }
                                if (limits[branch.id][channel.id] === undefined) {
                                    limits[branch.id][channel.id] = 150
                                }
                            })
                        })

                        return {
                            id: taskId,
                            name: availableTask ? (availableTask as any).name : taskId,
                            unit: availableTask ? (availableTask as any).unit : '',
                            norm: 0,
                            weight: 0,
                            maxCompletion: 150,
                            significance: 0, // Значимость будет установлена равной после создания всех задач
                            norms: norms,
                            limits: limits,
                        }
                    }
                })

                // Обновляем исходный план с синхронизированными задачами
            ;(props.plan as any).tasks = synchronizedTasks

            // Устанавливаем равные значения значимости для всех задач, если они не установлены
            setEqualSignificanceForTasks(synchronizedTasks)
        }

        // Методы для работы с филиалами
        const updateBranchName = (index: number, value: string) => {
            const oldBranchId = planData.value.branches[index].id
            const newBranchId = value.trim()

            // Обновляем название и ID
            planData.value.branches[index].name = value
            planData.value.branches[index].id = newBranchId

            // Если ID изменился, обновляем связанные данные
            if (oldBranchId !== newBranchId) {
                updateBranchDataId(oldBranchId, newBranchId)
            }
        }

        const updateBranchDataId = (oldBranchId: string, newBranchId: string) => {
            const plan = planData.value as any

            // Обновляем план выручки
            if (plan.dailyRevenuePlan && plan.dailyRevenuePlan[oldBranchId] !== undefined) {
                plan.dailyRevenuePlan[newBranchId] = plan.dailyRevenuePlan[oldBranchId]
                delete plan.dailyRevenuePlan[oldBranchId]
            }

            // Обновляем зарплату
            if (plan.salary && plan.salary[oldBranchId]) {
                plan.salary[newBranchId] = plan.salary[oldBranchId]
                delete plan.salary[oldBranchId]
            }

            // Обновляем шкалу выручки
            if (plan.branchRevenueScales && plan.branchRevenueScales[oldBranchId]) {
                plan.branchRevenueScales[newBranchId] = plan.branchRevenueScales[oldBranchId]
                delete plan.branchRevenueScales[oldBranchId]
            }

            // Также обновляем основную шкалу выручки
            if (plan.revenueScale && plan.revenueScale[oldBranchId]) {
                plan.revenueScale[newBranchId] = plan.revenueScale[oldBranchId]
                delete plan.revenueScale[oldBranchId]
            }

            // Обновляем данные о задачах
            if (plan.tasks && plan.tasks.length > 0) {
                plan.tasks.forEach((task: any) => {
                    if (task.norms && task.norms[oldBranchId]) {
                        task.norms[newBranchId] = task.norms[oldBranchId]
                        delete task.norms[oldBranchId]
                    }
                    if (task.limits && task.limits[oldBranchId]) {
                        task.limits[newBranchId] = task.limits[oldBranchId]
                        delete task.limits[oldBranchId]
                    }
                })
            }
        }

        const addBranchFromInput = () => {
            if (addBranchName.value.trim() && planData.value.branches.length < 5) {
                const newBranchId = addBranchName.value.trim()
                planData.value.branches.push({
                    id: newBranchId,
                    name: newBranchId,
                })

                // Добавляем данные по умолчанию для нового филиала
                addBranchDefaultData(newBranchId)

                addBranchName.value = ''
            }
        }

        // Универсальные функции для заполнения данных по умолчанию
        const getDefaultRevenueScale = () => [
            { revenue: 10000, bonus: 100, percentage: 1.0 },
            { revenue: 15000, bonus: 150, percentage: 1.0 },
            { revenue: 20000, bonus: 300, percentage: 1.5 },
            { revenue: 25000, bonus: 375, percentage: 1.5 },
            { revenue: 30000, bonus: 600, percentage: 2.0 },
            { revenue: 35000, bonus: 700, percentage: 2.0 },
            { revenue: 40000, bonus: 790, percentage: 1.98 },
            { revenue: 45000, bonus: 860, percentage: 1.91 },
            { revenue: 50000, bonus: 920, percentage: 1.84 },
            { revenue: 55000, bonus: 980, percentage: 1.78 },
            { revenue: 60000, bonus: 1040, percentage: 1.73 },
            { revenue: 65000, bonus: 1090, percentage: 1.68 },
            { revenue: 70000, bonus: 1140, percentage: 1.63 },
            { revenue: 75000, bonus: 1180, percentage: 1.57 },
            { revenue: 80000, bonus: 1240, percentage: 1.55 },
            { revenue: 85000, bonus: 1260, percentage: 1.48 },
            { revenue: 90000, bonus: 1300, percentage: 1.44 },
        ]

        const getDefaultKpiScale = () => {
            const kpiData = [
                [70.0, 30.0],
                [71.0, 33.0],
                [72.0, 36.0],
                [73.0, 39.0],
                [74.0, 42.0],
                [75.0, 45.0],
                [76.0, 48.0],
                [77.0, 51.0],
                [78.0, 54.0],
                [79.0, 57.0],
                [80.0, 60.0],
                [81.0, 63.0],
                [82.0, 66.0],
                [83.0, 69.0],
                [84.0, 72.0],
                [85.0, 75.0],
                [86.0, 78.0],
                [87.0, 81.0],
                [88.0, 84.0],
                [89.0, 87.0],
                [90.0, 90.0],
                [91.0, 91.0],
                [92.0, 92.0],
                [93.0, 93.0],
                [94.0, 94.0],
                [95.0, 95.0],
                [96.0, 96.0],
                [97.0, 97.0],
                [98.0, 98.0],
                [99.0, 99.0],
                [100.0, 100.0],
                [101.0, 101.0],
                [102.0, 102.0],
                [103.0, 103.0],
                [104.0, 104.0],
                [105.0, 105.0],
                [106.0, 106.0],
                [107.0, 107.0],
                [108.0, 108.0],
                [109.0, 109.0],
                [110.0, 110.0],
                [111.0, 111.0],
                [112.0, 112.0],
                [113.0, 113.0],
                [114.0, 114.0],
                [115.0, 115.0],
                [116.0, 116.0],
                [117.0, 117.0],
                [118.0, 118.0],
                [119.0, 119.0],
                [120.0, 120.0],
            ]

            return kpiData.map((data, index) => ({
                id: index,
                completion: data[0],
                bonusPercentage: data[1],
            }))
        }

        const getDefaultSalary = () => ({
            fixedPart: 700,
            kpiBonus: 1200,
        })

        // Универсальная функция для инициализации всех данных по умолчанию
        const initializeDefaultData = () => {
            const plan = planData.value as any

            // Инициализируем зарплату по умолчанию только если ее еще нет
            if (!plan.salary || (plan.salary.fixedPart === undefined && plan.salary.kpiBonus === undefined)) {
                plan.salary = getDefaultSalary()
            } else {
                // Заполняем только незаполненные поля зарплаты
                if (plan.salary.fixedPart === undefined) {
                    plan.salary.fixedPart = 700
                }
                if (plan.salary.kpiBonus === undefined) {
                    plan.salary.kpiBonus = 1200
                }
            }

            // Инициализируем общую шкалу выручки по умолчанию только если ее еще нет
            if (!plan.revenueScale || plan.revenueScale.length === 0) {
                plan.revenueScale = getDefaultRevenueScale()
            }

            // Инициализируем KPI шкалу по умолчанию только если ее еще нет
            if (!plan.kpiScale || plan.kpiScale.length === 0) {
                plan.kpiScale = getDefaultKpiScale()
            }

            // Инициализируем план выручки по умолчанию только если его еще нет
            if (!plan.dailyRevenuePlan) {
                plan.dailyRevenuePlan = {}
            }

            // Инициализируем данные по умолчанию для всех филиалов
            if (plan.branches && plan.branches.length > 0) {
                plan.branches.forEach((branch: any) => {
                    addBranchDefaultData(branch.id)
                })
            }

            // Принудительно обновляем реактивность
            triggerRef(planData)
        }

        const addBranchDefaultData = (branchId: string) => {
            const plan = planData.value as any

            // Добавляем план выручки по умолчанию только если его еще нет
            if (!plan.dailyRevenuePlan) {
                plan.dailyRevenuePlan = {}
            }
            if (plan.dailyRevenuePlan[branchId] === undefined) {
                plan.dailyRevenuePlan[branchId] = 0
            }

            // Добавляем зарплату по умолчанию только если ее еще нет
            if (!plan.salary) {
                plan.salary = getDefaultSalary()
            } else {
                // Заполняем только незаполненные поля зарплаты
                if (plan.salary.fixedPart === undefined) {
                    plan.salary.fixedPart = 700
                }
                if (plan.salary.kpiBonus === undefined) {
                    plan.salary.kpiBonus = 1200
                }
            }

            // Добавляем шкалу выручки по умолчанию только если ее еще нет
            if (!plan.branchRevenueScales) {
                plan.branchRevenueScales = {}
            }
            if (!plan.branchRevenueScales[branchId]) {
                plan.branchRevenueScales[branchId] = getDefaultRevenueScale()
            }

            // Также добавляем в основную шкалу выручки
            if (!plan.revenueScale) {
                // Если это первый филиал, создаем массив
                if (plan.branches && plan.branches.length === 1) {
                    plan.revenueScale = getDefaultRevenueScale()
                } else {
                    // Если несколько филиалов, создаем объект
                    plan.revenueScale = {}
                }
            }

            // Если revenueScale - это массив (один филиал), преобразуем в объект при добавлении второго филиала
            if (Array.isArray(plan.revenueScale) && plan.branches && plan.branches.length > 1) {
                const existingScale = plan.revenueScale
                plan.revenueScale = {}
                // Находим первый филиал и переносим данные
                if (plan.branches.length > 0) {
                    plan.revenueScale[plan.branches[0].id] = existingScale
                }
            }

            // Если revenueScale - это объект, добавляем данные для нового филиала
            if (!Array.isArray(plan.revenueScale) && !plan.revenueScale[branchId]) {
                plan.revenueScale[branchId] = getDefaultRevenueScale()
            }

            // Добавляем данные о задачах по умолчанию только если их еще нет
            if (plan.tasks && plan.tasks.length > 0) {
                plan.tasks.forEach((task: any) => {
                    if (!task.norms) {
                        task.norms = {}
                    }
                    if (!task.limits) {
                        task.limits = {}
                    }

                    // Добавляем данные для всех каналов только если их еще нет
                    plan.channels.forEach((channel: any) => {
                        if (!task.norms[branchId]) {
                            task.norms[branchId] = {}
                        }
                        if (!task.limits[branchId]) {
                            task.limits[branchId] = {}
                        }

                        // Устанавливаем значения по умолчанию только если их еще нет
                        if (task.norms[branchId][channel.id] === undefined) {
                            task.norms[branchId][channel.id] = 0
                        }
                        if (task.limits[branchId][channel.id] === undefined) {
                            task.limits[branchId][channel.id] = 150
                        }
                    })
                })
            }
        }

        const removeBranch = (index: number) => {
            // Первый филиал является базовым и не подлежит удалению
            if (index === 0) {
                return
            }

            if (planData.value.branches.length > 1) {
                const removedBranch = planData.value.branches[index]
                planData.value.branches.splice(index, 1)

                // Удаляем связанные данные из плана
                removeBranchData(removedBranch.id)
            }
        }

        const removeBranchData = (branchId: string) => {
            const plan = planData.value as any

            // Удаляем данные о выручке для филиала
            if (plan.dailyRevenuePlan && plan.dailyRevenuePlan[branchId]) {
                delete plan.dailyRevenuePlan[branchId]
            }

            // Удаляем данные о зарплате для филиала
            if (plan.salary && plan.salary[branchId]) {
                delete plan.salary[branchId]
            }

            // Удаляем данные о шкале выручки для филиала
            if (plan.branchRevenueScales && plan.branchRevenueScales[branchId]) {
                delete plan.branchRevenueScales[branchId]
            }

            // Также удаляем из основной шкалы выручки
            if (plan.revenueScale && plan.revenueScale[branchId]) {
                delete plan.revenueScale[branchId]
            }

            // Удаляем данные о задачах для филиала
            if (plan.tasks && plan.tasks.length > 0) {
                plan.tasks.forEach((task: any) => {
                    if (task.norms && task.norms[branchId]) {
                        delete task.norms[branchId]
                    }
                    if (task.limits && task.limits[branchId]) {
                        delete task.limits[branchId]
                    }
                })
            }
        }

        // Методы для работы с каналами
        const updateChannelName = (index: number, value: string) => {
            // Каналы скрыты, метод не используется
            const oldChannelId = planData.value.channels[index].id
            const newChannelId = value.trim()
            planData.value.channels[index].name = value
            planData.value.channels[index].id = newChannelId
            if (oldChannelId !== newChannelId) {
                updateChannelDataId(oldChannelId, newChannelId)
            }
        }

        const updateChannelDataId = (oldChannelId: string, newChannelId: string) => {
            // Каналы скрыты, метод не используется
        }

        const addChannelFromInput = () => {
            // Каналы скрыты, метод не используется
        }

        const addChannelDefaultData = (channelId: string) => {
            // Каналы скрыты, метод не используется
        }

        const removeChannel = (index: number) => {
            // Каналы скрыты, метод не используется
        }

        const removeChannelData = (channelId: string) => {
            // Каналы скрыты, метод не используется
        }

        // Методы для работы с пользователями
        const updateDhUser = (index: number, userId: string) => {
            const user = props.availableUsers.find((u: any) => u.id === userId) as any
            if (user) {
                planData.value.userMappings[index].dhUser = { id: user.id, name: user.name }
                // Обновляем отображаемое значение
                userDisplayValue.value[index] = user.name
            }
        }

        const updateCrmUser = (index: number, userId: string) => {
            const user = props.availableCrmUsers.find((u: any) => u.id === userId) as any
            if (user) {
                planData.value.userMappings[index].crmUser = { id: user.id, name: user.name }
                // Обновляем отображаемое значение
                crmUserDisplayValue.value[index] = user.name
            }
        }

        const addUserMapping = () => {
            planData.value.userMappings.push({
                dhUser: { id: '', name: '' },
                crmUser: { id: '', name: '' },
            })
            userDisplayValue.value.push('Выберите пользователя')
            crmUserDisplayValue.value.push('Выберите сотрудника')
        }

        const addUserMappingFromSelect = (userId: string, type: 'dh' | 'crm') => {
            if (userId) {
                if (type === 'dh') {
                    const user = props.availableUsers.find((u: any) => u.id === userId) as any
                    if (user) {
                        planData.value.userMappings.push({
                            dhUser: { id: user.id, name: user.name },
                            crmUser: { id: '', name: '' },
                        })
                        userDisplayValue.value.push(user.name)
                        crmUserDisplayValue.value.push('Выберите сотрудника')
                        addUserDisplayValue.value = 'Добавить ещё'
                    }
                } else {
                    const user = props.availableCrmUsers.find((u: any) => u.id === userId) as any
                    if (user) {
                        planData.value.userMappings.push({
                            dhUser: { id: '', name: '' },
                            crmUser: { id: user.id, name: user.name },
                        })
                        userDisplayValue.value.push('Выберите пользователя')
                        crmUserDisplayValue.value.push(user.name)
                        addCrmUserDisplayValue.value = 'Добавить ещё'
                    }
                }
            }
        }

        const removeUserMapping = (index: number) => {
            if (planData.value.userMappings.length > 1) {
                planData.value.userMappings.splice(index, 1)
                userDisplayValue.value.splice(index, 1)
                crmUserDisplayValue.value.splice(index, 1)
            }
        }

        // Функция для проверки и выравнивания суммы значимостей задач перед сохранением
        const validateAndAdjustTaskSignificance = () => {
            const tasks = planData.value.tasks.filter(task => task.id) // Только задачи с ID

            if (tasks.length === 0) {
                return
            }

            // Вычисляем текущую сумму значимостей
            const currentSum = tasks.reduce((sum, task) => sum + ((task as any).significance || 0), 0)

            // Если сумма не равна 100%, выравниваем её
            if (Math.abs(currentSum - 100) > 0.01) {
                // Вычисляем равную значимость для всех задач
                const equalSignificance = Math.round((100 / tasks.length) * 10) / 10

                // Устанавливаем равную значимость для всех задач
                tasks.forEach(task => {
                    ;(task as any).significance = equalSignificance
                })

                // Корректируем последнюю задачу, чтобы сумма была точно 100%
                const newSum = tasks.reduce((sum, task) => sum + ((task as any).significance || 0), 0)
                const difference = 100 - newSum

                if (tasks.length > 0 && Math.abs(difference) > 0.01) {
                    const lastTask = tasks[tasks.length - 1]
                    ;(lastTask as any).significance =
                        Math.round(((lastTask as any).significance + difference) * 10) / 10
                }
            }
        }

        const savePlan = () => {
            // Вызов сохранения плана

            if (isFormValid.value) {
                // Проверяем и выравниваем сумму значимостей задач перед сохранением
                validateAndAdjustTaskSignificance()

                // Преобразуем данные в формат, ожидаемый бэкендом
                // Получаем ID задач, которые остались в форме
                const currentTaskIds = planData.value.tasks.filter(task => task.id).map(task => task.id)

                // Заполняем limits значениями по умолчанию для всех задач
                planData.value.tasks.forEach((task: any) => {
                    if (!task.limits) {
                        task.limits = {}
                    }

                    // Получаем все филиалы
                    const branches = planData.value.branches
                        .filter(branch => branch.name.trim())
                        .map(branch => branch.id)

                    // Если нет филиалов, используем 'general'
                    const branchIds = branches.length > 0 ? branches : ['general']
                    const channelIds = ['general']

                    // Заполняем limits для всех комбинаций филиал-канал
                    branchIds.forEach(branchId => {
                        if (!task.limits[branchId]) {
                            task.limits[branchId] = {}
                        }
                        channelIds.forEach(channelId => {
                            if (
                                task.limits[branchId][channelId] === undefined ||
                                task.limits[branchId][channelId] === null
                            ) {
                                task.limits[branchId][channelId] = 150 // Значение по умолчанию
                            }
                        })
                    })
                })

                const saveData = {
                    name: planData.value.name,
                    taskIds: currentTaskIds,
                    branchIds: planData.value.branches
                        .filter(branch => branch.name.trim())
                        .map(branch => branch.id),
                    channelIds: planData.value.channels
                        .filter(channel => channel.name.trim())
                        .map(channel => channel.id),
                    userIds: planData.value.userMappings
                        .filter(mapping => mapping.dhUser.id)
                        .map(mapping => mapping.dhUser.id),
                    // Передаем userMappings для сохранения соответствий
                    userMappings: planData.value.userMappings.map(mapping => ({
                        dhUser: { id: mapping.dhUser.id, name: mapping.dhUser.name },
                        crmUser: { id: mapping.crmUser.id, name: mapping.crmUser.name },
                    })),

                    // Включаем все дополнительные данные из исходного плана (для копирования)
                    ...(props.plan && {
                        // План по дневной выручке
                        dailyRevenuePlan: (props.plan as any).dailyRevenuePlan,
                        generalDailyRevenuePlan: (props.plan as any).generalDailyRevenuePlan,

                        // Данные о зарплате и бонусах
                        salary: (props.plan as any).salary,
                        kpiScale: (props.plan as any).kpiScale,
                        fixedDailySalary: (props.plan as any).fixedDailySalary,
                        dailyKpiBonus: (props.plan as any).dailyKpiBonus,

                        // Нормы и веса задач - фильтруем только те задачи, которые остались в форме
                        tasks: planData.value.tasks.filter((task: any) => currentTaskIds.includes(task.id)),

                        // Шкала выручки - используем из planData, который уже содержит правильную структуру
                        revenueScale: (planData.value as any).revenueScale,

                        // Пользователи
                        users: (props.plan as any).users,
                    }),

                    // Для новых планов добавляем значения по умолчанию из planData
                    ...(!props.plan?.id && {
                        salary: (planData.value as any).salary,
                        revenueScale: (planData.value as any).revenueScale,
                        kpiScale: (planData.value as any).kpiScale,
                        dailyRevenuePlan: (planData.value as any).dailyRevenuePlan,
                        branchRevenueScales: (planData.value as any).branchRevenueScales,
                    }),
                }

                emit('save', saveData)
            } else {
                // Форма не валидна, сохранение невозможно
            }
        }

        const cancel = () => {
            emit('close')
        }

        const openTaskLibrary = () => {
            const router = instance?.proxy?.$router
            if (!router || !props.projectId) return
            const route = router.resolve({
                name: 'BeautyAutopilotTaskLibrary',
                params: { id: props.projectId },
            })
            window.open(route.href, '_blank')
        }

        const handleCreateTask = (taskData: any) => {
            emit('create-task', taskData)
        }

        const saveTask = async () => {
            if (selectedTask.value && selectedTask.value.name) {
                const taskIndex = selectedTask.value?.originalTaskIndex

                // Проверяем, является ли задача общей (без projectId)
                const availableTask = Array.isArray(props.availableTasks)
                    ? (props.availableTasks as any[]).find((t: any) => t.id === selectedTask.value.id)
                    : null
                const isGlobalTask =
                    availableTask &&
                    ((availableTask as any).projectId === null ||
                        (availableTask as any).project_id === null)

                // Если задача общая и редактируется, создаем копию, привязанную к проекту
                if (isGlobalTask && taskIndex !== undefined && taskIndex !== -1 && taskIndex >= 0) {
                    if (props.createTaskFn) {
                        try {
                            // Создаем копию общей задачи, привязанную к текущему проекту
                            const createdTask = await props.createTaskFn({
                                name: selectedTask.value.name,
                                unit: selectedTask.value.unit || '',
                                description: selectedTask.value.description || '',
                                autofill_params: selectedTask.value.autofillParams || null,
                                task_number:
                                    selectedTask.value.taskNumber ||
                                    (availableTask as any)?.task_number ||
                                    (availableTask as any)?.taskNumber,
                            })

                            if (createdTask) {
                                // Обновляем задачу в плане на новую копию
                                const updatedTaskInPlan = {
                                    ...planData.value.tasks[taskIndex],
                                    id: createdTask.id,
                                    name: createdTask.name,
                                    unit: createdTask.unit,
                                    description:
                                        createdTask.description || selectedTask.value.description || '',
                                    taskNumber: createdTask.task_number || createdTask.taskNumber,
                                    autofillParams:
                                        createdTask.autofill_params ||
                                        createdTask.autofillParams ||
                                        selectedTask.value.autofillParams,
                                } as any

                                planData.value.tasks[taskIndex] = updatedTaskInPlan
                                taskDisplayValue.value[taskIndex] = createdTask.name

                                // Парсим autofillParams для добавления в список доступных задач
                                let autofillParamsParsed =
                                    createdTask.autofill_params || createdTask.autofillParams
                                if (autofillParamsParsed && typeof autofillParamsParsed === 'string') {
                                    try {
                                        autofillParamsParsed = JSON.parse(autofillParamsParsed)
                                    } catch (e) {
                                        autofillParamsParsed = null
                                    }
                                }

                                // Задача уже добавлена в availableTasks через createTaskFn в родительском компоненте
                                triggerRef(planData)
                            }
                        } catch (error) {
                            console.error('Error creating task copy:', String(error))
                        }
                    }
                } else if (taskIndex !== undefined && taskIndex !== -1 && taskIndex >= 0) {
                    // Обновляем существующую задачу в плане (не общую)
                    const updatedTask = {
                        ...planData.value.tasks[taskIndex],
                        name: selectedTask.value.name,
                        unit: selectedTask.value.unit || '',
                        description: selectedTask.value.description || '',
                        autofillParams: selectedTask.value.autofillParams || null,
                    }

                    planData.value.tasks[taskIndex] = updatedTask as any
                    taskDisplayValue.value[taskIndex] = selectedTask.value.name
                    triggerRef(planData)
                }

                // Если есть функция создания задачи, вызываем её (для новых задач)
                if (props.createTaskFn && taskIndex === -1) {
                    try {
                        // Создаем новую задачу через переданную функцию
                        const createdTask = await props.createTaskFn({
                            name: selectedTask.value.name,
                            unit: selectedTask.value.unit || '',
                            description: selectedTask.value.description || '',
                            autofill_params: selectedTask.value.autofillParams || null,
                        })

                        if (createdTask) {
                            if (planData.value.tasks.length < 5) {
                                // Добавляем задачу через addTaskFromSelect, чтобы она сразу выбралась
                                addTaskFromSelect(createdTask.id)
                            }

                            // Обновляем доступные задачи
                            triggerRef(planData)
                        }
                    } catch (error) {
                        console.error('Error creating task:', String(error))
                    }
                }
            }

            showTaskEditModal.value = false
            selectedTask.value = null
        }

        // Computed свойство для фильтрации доступных задач (исключаем уже выбранные, оставляем только задачи текущего проекта и общие)
        const filteredAvailableTasks = computed(() => {
            const selectedTaskIds = planData.value.tasks.map(task => task.id).filter(id => id)
            return props.availableTasks.filter((task: any) => {
                // Исключаем уже выбранные задачи
                if (selectedTaskIds.includes(task.id)) return false

                // Оставляем только задачи текущего проекта или общие задачи (projectId === null)
                return task.projectId === props.projectId || task.projectId === null
            })
        })

        // Метод для получения полной информации о выбранной задаче
        const getSelectedTask = (taskId: string) => {
            return props.availableTasks.find((t: any) => t.id === taskId) || null
        }

        // Методы для фильтрации доступных пользователей (исключаем уже выбранных)
        const getAvailableDhUsers = (currentIndex: number) => {
            // Получаем список уже выбранных пользователей DH (кроме текущего индекса)
            const selectedDhUserIds = planData.value.userMappings
                .map((mapping, idx) => (idx !== currentIndex && mapping.dhUser.id ? mapping.dhUser.id : null))
                .filter(id => id !== null && id !== '')
            
            // Фильтруем доступных пользователей, исключая уже выбранных
            return (props.availableUsers as Array<{ id: string; name: string }>).filter(
                user => !selectedDhUserIds.includes(user.id)
            )
        }

        const getAvailableCrmUsers = (currentIndex: number) => {
            // Проверяем наличие коннектора yClients и его статус
            if (!yClientsConnectionStatus.value) {
                return []
            }

            // Если статус не 'active', возвращаем пустой список
            if (yClientsConnectionStatus.value !== 'active') {
                return []
            }

            // Получаем список уже выбранных сотрудников УС (кроме текущего индекса)
            const selectedCrmUserIds = planData.value.userMappings
                .map((mapping, idx) => (idx !== currentIndex && mapping.crmUser.id ? mapping.crmUser.id : null))
                .filter(id => id !== null && id !== '')
            
            // Фильтруем доступных сотрудников, исключая уже выбранных
            return (props.availableCrmUsers as Array<{ id: string; name: string }>).filter(
                user => !selectedCrmUserIds.includes(user.id)
            )
        }

        return {
            isVisible,
            isEdit,
            isReadOnly,
            planData,
            isFormValid,
            taskDisplayValue,
            userDisplayValue,
            crmUserDisplayValue,
            addTaskDisplayValue,
            addUserDisplayValue,
            addCrmUserDisplayValue,
            addBranchName,
            updateTask,
            addTask,
            addTaskFromSelect,
            removeTask,
            synchronizeTasksWithTaskIds,
            setEqualSignificanceForTasks,
            validateAndAdjustTaskSignificance,
            updateBranchName,
            updateBranchDataId,
            addBranchFromInput,
            addBranchDefaultData,
            removeBranch,
            removeBranchData,
            updateChannelName: () => {},
            updateChannelDataId: () => {},
            addChannelFromInput: () => {},
            addChannelDefaultData: () => {},
            removeChannel: () => {},
            removeChannelData: () => {},
            updateDhUser,
            updateCrmUser,
            addUserMapping,
            addUserMappingFromSelect,
            removeUserMapping,
            savePlan,
            cancel,
            openTaskLibrary,
            handleCreateTask,
            filteredAvailableTasks,
            getSelectedTask,
            getAvailableDhUsers,
            getAvailableCrmUsers,
            hasYClientsConnection,
            yClientsConnectionStatus,
            editTask,
            saveTask,
            showTaskEditModal,
            selectedTask,
            addNewTask,
            deleteTaskFromSelector,
            availableServices,
            hasServiceVariables,
            needsVisitedAndBookedServices,
            needsRecordServiceList,
            removeService,
        }
    },
})
